'use client';

import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'usehooks-ts';

import { LANGUAGES } from '@/components/LanguageSwitcher/LanguageSwitcher.constants';
import { DropdownArrowIcon, GlobeIcon } from '@/components/ui/Icons/Icons';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { LocalesEnum } from '@/constants/locales';
import { getLocaleUrl } from '@/utils/helpers/locales';

import * as S from './LanguageSwitcher.styles';

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  const currentLanguage = i18n.language;

  const toggleDropdown = () => {
    setIsOpen(prevState => !prevState);
  };

  const handleLanguageChange = (newLocale: LocalesEnum) => {
    const { protocol, host, pathname, search } = window.location;

    const theme =
      document.documentElement.getAttribute('data-theme') || 'light';
    const currentDomain = window.location.hostname
      .split('.')
      .slice(-2)
      .join('.');
    Cookies.set('theme', theme, {
      path: '/',
      domain: currentDomain,
      expires: 30
    });

    const newUrl = getLocaleUrl({
      protocol,
      host,
      pathname,
      search,
      newLocale
    });

    router.push(newUrl);
    setIsOpen(false);
  };

  useOnClickOutside(switcherRef, () => setIsOpen(false));

  return (
    <S.Wrapper ref={switcherRef} $isOpen={isOpen}>
      <S.Dropdown role="listbox" $isOpen={isOpen}>
        {LANGUAGES.map(lang => (
          <S.Option
            role="option"
            key={lang.code}
            $isSelected={lang.code === currentLanguage}
            onClick={() => handleLanguageChange(lang.code)}
          >
            <Typography variant="body2">{lang.label}</Typography>
          </S.Option>
        ))}
      </S.Dropdown>
      <S.SelectedLanguage onClick={toggleDropdown}>
        <GlobeIcon />
        <Typography data-testid="language-switcher-button" variant="body3">
          {LANGUAGES.find(lang => lang.code === currentLanguage)?.label}
        </Typography>
        <S.Arrow $isOpen={isOpen}>
          <DropdownArrowIcon />
        </S.Arrow>
      </S.SelectedLanguage>
    </S.Wrapper>
  );
};
